import React, { useEffect, useState } from 'react';
import { fetchSessions } from '../services/api';

interface OverviewData {
  content: string;
}

const ConferenceOverview: React.FC = () => {
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentResponse, sessionsData] = await Promise.all([
          fetch('https://languageintelligence-backend.bartoszbudzynski.pl/public/hero-content')
            .then(response => {
              if (!response.ok) throw new Error('Network response failed');
              return response.json();
            }),
          fetchSessions()
        ]);
        
        setOverviewData(contentResponse);
        setSessions(sessionsData.sessions || []);
        setLoading(false);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) return <div className="text-blue-500 p-4">Error: {error}</div>;

  return (
    <section className="py-24 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="animate-pulse text-lg text-center">Loading overview...</div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              {/* Left side: Visual element */}
              <div className="relative">
                <div className="absolute inset-0 bg-[#00529B]/10 rounded-2xl transform -rotate-3"></div>
                <div className="relative bg-gradient-to-br from-[#00529B] to-[#003D74] p-8 rounded-2xl shadow-xl">
                  <h2 className="text-4xl font-bold text-white mb-6">Why Join Us?</h2>
                  <ul className="space-y-4 text-white/90">
                    <li className="flex items-start">
                      <svg className="w-6 h-6 text-white mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Connect with industry leaders and innovators</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 text-white mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Explore cutting-edge AI technologies</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-6 h-6 text-white mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Shape the future of human-machine communication</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right side: Content */}
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-[#00529B] mb-8">
                  Discover Language Intelligence
                </h3>
                <div
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: overviewData?.content
                      ? overviewData.content
                          .split('\n\n')
                          .map(paragraph => `<p>${paragraph
                            .replace(/(Language Intelligence)/g, '<strong class="text-[#00529B]">$1</strong>')
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</p>`)
                          .join('\n')
                      : ''
                  }}
                />
                <div className="mt-8 flex gap-4">
                  {sessions.length > 0 && (
                    <a href="/schedule" className="bg-[#00529B] hover:bg-[#003D74] text-white px-6 py-3 rounded-full transition-colors inline-block text-center">
                      View Schedule
                    </a>
                  )}
                  <a href="/about" className="border-2 border-[#00529B] text-[#00529B] hover:bg-[#00529B] hover:text-white px-6 py-3 rounded-full transition-colors inline-block text-center">
                    Learn more
                  </a>
                </div>
              </div>
            </div>

            {/* Venue Information Section */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#00529B] mb-6">Conference Venue</h3>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img 
                    src="/parliment-1.jpg" 
                    alt="Conference Venue" 
                    className="rounded-lg shadow-md w-full h-auto object-cover"
                  />
                </div>
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-[#00529B]">Austrian Parliament</h4>
                  <p className="text-gray-600">
                    Join us at the prestigious Austrian Parliament, located in the heart of the city.
                    The venue offers state-of-the-art facilities and is easily accessible by public transportation.
                  </p>
                  <div className="mt-6 flex space-x-4">
                    <a 
                      href="https://maps.app.goo.gl/Q59RTMaQaXtvr31k7" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-[#00529B] text-white rounded-md hover:bg-[#003D74] transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      View on Google Maps
                    </a>
                    <a 
                      href="/location" 
                      className="inline-flex items-center px-4 py-2 border border-[#00529B] text-[#00529B] rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ConferenceOverview;
